/**
 * Cliente HTTP tipado alinhado ao Api.js Vanilla.
 *
 * - Access `_at` / refresh `_rt` (SimpleJWT com rotação)
 * - 401 → um único POST /api/token/refresh/ e retry
 * - Logout → blacklist do refresh em /api/v1/accounts/logout/
 * - Headers: Authorization, X-App-Client, X-Request-ID
 */

import {
  clearSession,
  getAccessToken,
  getRefreshToken,
  setStoredUser,
  setTokens,
} from './session'

export {
  clearSession,
  getAccessToken,
  getRefreshToken,
  getStoredUser,
  hasToken,
  setStoredUser,
  setTokens,
  SESSION_KEYS,
  subscribeSession,
  legacyBasePath,
  legacyLoginUrl,
  legacyAppUrl,
  microsoftLoginUrl,
} from './session'

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) || '/api/v1'
const APP_CLIENT = 'hi-conecta-rh'
const TIMEOUT_MS = 15_000

export class ApiError extends Error {
  status: number
  data: unknown
  constructor(message: string, status = 500, data: unknown = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

export type AuthUser = {
  id?: string | number
  email?: string
  nome?: string
  name?: string
  perfil?: string
  perfil_display?: string
  iniciais?: string
  foto?: string | null
  foto_url?: string
  colaborador_id?: string | null
  cargo_descritivo?: string | null
  [key: string]: unknown
}

type RequestOpts = RequestInit & {
  auth?: boolean
  skipRefresh?: boolean
  /** Omite indicadores de loading futuros / polls em background. */
  quiet?: boolean
  /** Timeout em ms (padrão 15s). */
  timeoutMs?: number
}

function uuid() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function url(path: string) {
  if (/^https?:\/\//i.test(path)) return path
  const base = API_BASE.replace(/\/$/, '')
  const p = path.startsWith('/') ? path : `/${path}`
  if (p.startsWith('/api/')) return p
  return `${base}${p}`
}

function extractMessage(json: unknown, fallback: string) {
  if (!json || typeof json !== 'object') return fallback
  const o = json as Record<string, unknown>
  if (o.mensagem || o.message || o.detail || o.erro) {
    return String(o.mensagem || o.message || o.detail || o.erro)
  }
  if (o.erros) return typeof o.erros === 'string' ? o.erros : JSON.stringify(o.erros)
  return fallback
}

function unwrapUsuario(payload: unknown): AuthUser | null {
  if (!payload || typeof payload !== 'object') return null
  const o = payload as Record<string, unknown>
  if (o.usuario && typeof o.usuario === 'object') return o.usuario as AuthUser
  if (o.user && typeof o.user === 'object') return o.user as AuthUser
  if (o.email || o.nome || o.perfil) return o as AuthUser
  return null
}

let refreshing: Promise<string> | null = null

async function refreshAccessToken(): Promise<string> {
  if (refreshing) return refreshing
  const atual = getRefreshToken()
  if (!atual) throw new ApiError('Sessão expirada. Faça login novamente.', 401)

  refreshing = fetch(url('/api/token/refresh/'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-App-Client': APP_CLIENT,
      'X-Request-ID': uuid(),
    },
    body: JSON.stringify({ refresh: atual }),
  })
    .then(async (res) => {
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new ApiError('Sessão expirada. Faça login novamente.', 401, data)
      const access = (data as { access?: string; access_token?: string }).access ||
        (data as { access_token?: string }).access_token
      if (!access) throw new ApiError('Sessão expirada. Faça login novamente.', 401, data)
      const nextRefresh = (data as { refresh?: string }).refresh
      setTokens(access, nextRefresh)
      return access
    })
    .finally(() => {
      refreshing = null
    })

  return refreshing
}

async function parseBody(res: Response) {
  if (res.status === 204) return { success: true, data: null }
  const text = await res.text()
  if (!text) return {}
  try {
    return JSON.parse(text) as unknown
  } catch {
    return { detail: text }
  }
}

async function request<T = unknown>(endpoint: string, opts: RequestOpts = {}): Promise<T> {
  const {
    auth = true,
    skipRefresh = false,
    quiet: _quiet,
    timeoutMs = TIMEOUT_MS,
    headers: extra,
    ...rest
  } = opts
  void _quiet

  const headers = new Headers(extra || {})
  if (!headers.has('Accept')) headers.set('Accept', 'application/json')
  if (!headers.has('X-App-Client')) headers.set('X-App-Client', APP_CLIENT)
  if (!headers.has('X-Request-ID')) headers.set('X-Request-ID', uuid())

  if (rest.body && !(rest.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (auth) {
    const at = getAccessToken()
    if (at) headers.set('Authorization', `Bearer ${at}`)
  }

  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)

  let res: Response
  try {
    res = await fetch(url(endpoint), { ...rest, headers, signal: ctrl.signal })
  } catch (err) {
    clearTimeout(timer)
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError('Servidor demorou muito para responder.', 408)
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw new ApiError('Sem conexão com a internet.', 0)
    }
    throw new ApiError(err instanceof Error ? err.message : 'Erro de comunicação com o servidor.', 0)
  } finally {
    clearTimeout(timer)
  }

  if (res.status === 401 && auth && !skipRefresh && !String(endpoint).includes('/token/refresh')) {
    try {
      await refreshAccessToken()
      return request<T>(endpoint, { ...opts, skipRefresh: true })
    } catch {
      clearSession()
      throw new ApiError('Sessão expirada. Faça login novamente.', 401)
    }
  }

  const data = await parseBody(res)

  if (res.status === 403) {
    throw new ApiError(extractMessage(data, 'Sem permissão para esta ação.'), 403, data)
  }
  if (res.status === 429) {
    throw new ApiError(
      extractMessage(data, 'Muitas requisições. Tente novamente em breve.'),
      429,
      data,
    )
  }
  if (!res.ok) {
    throw new ApiError(extractMessage(data, `Erro HTTP ${res.status}`), res.status, data)
  }

  return data as T
}

async function upload<T = unknown>(endpoint: string, formData: FormData, opts: RequestOpts = {}) {
  return request<T>(endpoint, {
    ...opts,
    method: 'POST',
    body: formData,
    headers: {
      ...(opts.headers || {}),
      // sem Content-Type — browser define boundary multipart
    },
  })
}

export const api = {
  get: <T = unknown>(path: string, opts?: RequestOpts) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T = unknown>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>(path, {
      ...opts,
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
    }),
  put: <T = unknown>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>(path, { ...opts, method: 'PUT', body: JSON.stringify(body ?? {}) }),
  patch: <T = unknown>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>(path, { ...opts, method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  delete: <T = unknown>(path: string, opts?: RequestOpts) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
  upload,
  request: <T = unknown>(method: string, path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>(path, {
      ...opts,
      method,
      body:
        body == null
          ? undefined
          : body instanceof FormData
            ? body
            : JSON.stringify(body),
    }),
}

export async function loginWithPassword(email: string, password: string) {
  const data = await api.post<{
    sucesso?: boolean
    access?: string
    refresh?: string
    access_token?: string
    refresh_token?: string
    usuario?: AuthUser
    user?: AuthUser
    mensagem?: string
  }>('/accounts/login/', { email, password }, { auth: false })

  const access = data.access || data.access_token
  const refresh = data.refresh || data.refresh_token
  if (!access) throw new ApiError('Resposta de login sem access token.', 500, data)

  setTokens(access, refresh)

  let user = unwrapUsuario(data)
  if (!user) {
    try {
      user = await fetchMe()
    } catch {
      user = { email }
    }
  } else {
    setStoredUser(user)
  }
  return user
}

/**
 * Consome o retorno do Entra ID (`#access_token=…` ou `?sso_erro=`).
 * Retorna o usuário se o fragmento trouxe sessão; senão null.
 */
export function consumeMicrosoftSsoReturn(): {
  user: AuthUser | null
  erro: string | null
} {
  if (typeof window === 'undefined') return { user: null, erro: null }

  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash
  const hashParams = new URLSearchParams(hash)
  const access = hashParams.get('access_token')

  if (access) {
    const refresh = hashParams.get('refresh_token') || undefined
    setTokens(access, refresh)
    const perfilRaw = hashParams.get('perfil') || 'colab'
    const perfil =
      perfilRaw === 'colaborador' ? 'colab' : perfilRaw
    const user: AuthUser = {
      id: hashParams.get('id') || undefined,
      nome: (hashParams.get('nome') || '').slice(0, 200),
      email: (hashParams.get('email') || '').slice(0, 200),
      perfil,
      perfil_display: hashParams.get('perfil_display') || undefined,
      colaborador_id: hashParams.get('colaborador_id') || undefined,
      foto_url: hashParams.get('foto_url') || undefined,
      _via_api: true,
    }
    setStoredUser(user)
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}`,
    )
    return { user, erro: null }
  }

  const search = new URLSearchParams(window.location.search)
  const erro = search.get('sso_erro')
  if (erro) {
    search.delete('sso_erro')
    const qs = search.toString()
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${qs ? `?${qs}` : ''}`,
    )
    return { user: null, erro: String(erro).slice(0, 300) }
  }

  return { user: null, erro: null }
}

export async function logout() {
  const refresh = getRefreshToken()
  try {
    await api.post('/accounts/logout/', refresh ? { refresh_token: refresh } : {}, { auth: true })
  } catch {
    /* limpa local mesmo se a rede falhar */
  }
  clearSession()
}

export async function fetchMe() {
  const payload = await api.get<unknown>('/accounts/me/')
  const me = unwrapUsuario(payload)
  if (!me) throw new ApiError('Resposta /me inválida.', 500, payload)
  setStoredUser(me)
  return me
}

/** Força refresh do access token (útil em smoke tests / bridge). */
export async function forceRefresh() {
  return refreshAccessToken()
}

export { API_BASE, APP_CLIENT }
